import jsPDF from 'jspdf';
import { BrokerAgreementWithDetails } from '../types';
import { toast } from 'sonner';

/**
 * Helper function to detect if text is Hebrew (RTL)
 */
function isHebrewText(text: string): boolean {
  if (!text || text.trim().length === 0) return false;
  const firstChar = text.trim().charAt(0);
  return /[\u0590-\u05FF]/.test(firstChar);
}

/**
 * Convert image URL to base64
 */
async function imageUrlToBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return null;
  }
}

/**
 * Split text into lines that fit within page width
 */
function splitTextIntoLines(
  doc: jsPDF,
  text: string,
  maxWidth: number
): string[] {
  const lines: string[] = [];
  const words = text.split(/\s+/);
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const textWidth = doc.getTextWidth(testLine);

    if (textWidth > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

/**
 * Generate PDF from agreement data
 */
export async function generateAgreementPDF(
  agreement: BrokerAgreementWithDetails,
  signatureImageUrl: string | null
): Promise<Blob> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPosition = margin;

  const agreementText = agreement.agreement_text || '';
  const isRTL = isHebrewText(agreementText);

  // Set font based on language
  const fontFamily = isRTL ? 'Arial' : 'Georgia';
  doc.setFont(fontFamily);

  // Title
  doc.setFontSize(18);
  doc.setFont(fontFamily, 'bold');
  const title = isRTL ? 'הסכם בלעדיות מתווך' : 'BROKER EXCLUSIVITY AGREEMENT';
  const titleWidth = doc.getTextWidth(title);
  const titleX = isRTL ? pageWidth - margin - titleWidth : margin;
  doc.text(title, titleX, yPosition, { align: isRTL ? 'right' : 'left' });
  yPosition += 10;

  // Agreement text
  doc.setFontSize(10);
  doc.setFont(fontFamily, 'normal');
  const lines = splitTextIntoLines(doc, agreementText, contentWidth);

  for (const line of lines) {
    // Check if we need a new page
    if (yPosition > pageHeight - margin - 40) {
      doc.addPage();
      yPosition = margin;
    }

    const lineX = isRTL ? pageWidth - margin : margin;
    doc.text(line, lineX, yPosition, { align: isRTL ? 'right' : 'left' });
    yPosition += 6;
  }

  // Add spacing before signature
  yPosition += 10;

  // Signature image
  if (signatureImageUrl) {
    try {
      const base64Image = await imageUrlToBase64(signatureImageUrl);
      if (base64Image) {
        // Check if we need a new page for signature
        if (yPosition > pageHeight - margin - 60) {
          doc.addPage();
          yPosition = margin;
        }

        // Add signature label
        doc.setFontSize(10);
        doc.setFont(fontFamily, 'bold');
        const signatureLabel = isRTL ? 'חתימה:' : 'Signature:';
        const labelWidth = doc.getTextWidth(signatureLabel);
        const labelX = isRTL ? pageWidth - margin - labelWidth : margin;
        doc.text(signatureLabel, labelX, yPosition, { align: isRTL ? 'right' : 'left' });
        yPosition += 8;

        // Add signature image (max 40mm height)
        const imgWidth = 60;
        const imgHeight = 20;
        const imgX = isRTL ? pageWidth - margin - imgWidth : margin;
        doc.addImage(base64Image, 'PNG', imgX, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + 10;
      }
    } catch (error) {
      console.error('Error adding signature image to PDF:', error);
    }
  }

  // Signer information
  if (yPosition > pageHeight - margin - 30) {
    doc.addPage();
    yPosition = margin;
  }

  doc.setFontSize(10);
  doc.setFont(fontFamily, 'normal');

  if (agreement.renter_name) {
    const nameLabel = isRTL ? 'שם החותם/ת:' : 'Signer Name:';
    const nameText = `${nameLabel} ${agreement.renter_name}`;
    const nameWidth = doc.getTextWidth(nameText);
    const nameX = isRTL ? pageWidth - margin - nameWidth : margin;
    doc.text(nameText, nameX, yPosition, { align: isRTL ? 'right' : 'left' });
    yPosition += 6;
  }

  if (agreement.renter_id_number) {
    const idLabel = isRTL ? 'מספר זהות:' : 'ID Number:';
    const idText = `${idLabel} ${agreement.renter_id_number}`;
    const idWidth = doc.getTextWidth(idText);
    const idX = isRTL ? pageWidth - margin - idWidth : margin;
    doc.text(idText, idX, yPosition, { align: isRTL ? 'right' : 'left' });
    yPosition += 6;
  }

  if (agreement.signed_at) {
    const dateLabel = isRTL ? 'תאריך:' : 'Date:';
    const dateText = `${dateLabel} ${new Date(agreement.signed_at).toLocaleString()}`;
    const dateWidth = doc.getTextWidth(dateText);
    const dateX = isRTL ? pageWidth - margin - dateWidth : margin;
    doc.text(dateText, dateX, yPosition, { align: isRTL ? 'right' : 'left' });
  }

  // Generate PDF blob
  const pdfBlob = doc.output('blob');
  return pdfBlob;
}

/**
 * Share agreement as PDF using Web Share API or download fallback
 */
export async function shareAgreementAsPDF(
  agreement: BrokerAgreementWithDetails,
  signatureImageUrl: string | null
): Promise<void> {
  try {
    // Generate PDF
    const pdfBlob = await generateAgreementPDF(agreement, signatureImageUrl);
    const pdfFile = new File(
      [pdfBlob],
      `agreement-${agreement.id}.pdf`,
      { type: 'application/pdf' }
    );

    // Try Web Share API first (mobile and modern browsers)
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
      try {
        await navigator.share({
          title: 'Broker Exclusivity Agreement',
          text: 'Signed broker exclusivity agreement',
          files: [pdfFile]
        });
        toast.success('Agreement shared successfully');
        return;
      } catch (shareError: any) {
        // User cancelled or share failed, fall through to download
        if (shareError.name === 'AbortError') {
          return; // User cancelled, don't show error
        }
        console.warn('Web Share API failed, falling back to download:', shareError);
      }
    }

    // Fallback: Download the PDF
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agreement-${agreement.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Agreement downloaded successfully');
  } catch (error: any) {
    console.error('Error sharing agreement as PDF:', error);
    toast.error('Failed to generate or share PDF');
    throw error;
  }
}

